import { normalizeLocale, type SupportedLocale } from './config';

type NotificationCopy = {
  notifyAbout: string;
  all_messages: string;
  mentions: string;
  nothing: string;
  indefinite: string;
  newMessage: string;
};

const COPY: Record<SupportedLocale, NotificationCopy> = {
  en: {
    notifyAbout: 'Notify me about',
    all_messages: 'All messages',
    mentions: 'Only @mentions',
    nothing: 'Nothing',
    indefinite: 'Until I turn it back on',
    newMessage: 'New message in {chat}',
  },
  'pt-BR': {
    notifyAbout: 'Notificar sobre',
    all_messages: 'Todas as mensagens',
    mentions: 'S\u00F3 @men\u00E7\u00F5es',
    nothing: 'Nada',
    indefinite: 'At\u00E9 eu reativar',
    newMessage: 'Nova mensagem em {chat}',
  },
  ar: {
    notifyAbout: 'إشعاري بشأن',
    all_messages: 'كل الرسائل',
    mentions: 'الإشارات إليّ فقط',
    nothing: 'لا شيء',
    indefinite: 'حتى أعيد تشغيله',
    newMessage: 'رسالة جديدة في {chat}',
  },
  es: {
    notifyAbout: 'Notificarme sobre',
    all_messages: 'Todos los mensajes',
    mentions: 'Solo @menciones',
    nothing: 'Nada',
    indefinite: 'Hasta que lo reactive',
    newMessage: 'Nuevo mensaje en {chat}',
  },
  fr: {
    notifyAbout: 'Me notifier pour',
    all_messages: 'Tous les messages',
    mentions: 'Uniquement les @mentions',
    nothing: 'Rien',
    indefinite: 'Jusqu’à réactivation',
    newMessage: 'Nouveau message dans {chat}',
  },
  it: {
    notifyAbout: 'Notificami per',
    all_messages: 'Tutti i messaggi',
    mentions: 'Solo @menzioni',
    nothing: 'Niente',
    indefinite: 'Finché non riattivo',
    newMessage: 'Nuovo messaggio in {chat}',
  },
  ko: {
    notifyAbout: '알림 대상',
    all_messages: '모든 메시지',
    mentions: '@멘션만',
    nothing: '없음',
    indefinite: '다시 켤 때까지',
    newMessage: '{chat}에 새 메시지',
  },
  de: {
    notifyAbout: 'Benachrichtigen bei',
    all_messages: 'Allen Nachrichten',
    mentions: 'Nur @Erwähnungen',
    nothing: 'Nichts',
    indefinite: 'Bis ich es wieder einschalte',
    newMessage: 'Neue Nachricht in {chat}',
  },
  ja: {
    notifyAbout: '通知する内容',
    all_messages: 'すべてのメッセージ',
    mentions: '@メンションのみ',
    nothing: '通知しない',
    indefinite: '再びオンにするまで',
    newMessage: '{chat}に新しいメッセージ',
  },
  hi: {
    notifyAbout: 'इनके लिए सूचित करें',
    all_messages: 'सभी संदेश',
    mentions: 'केवल @उल्लेख',
    nothing: 'कोई नहीं',
    indefinite: 'जब तक मैं फिर से चालू न करूँ',
    newMessage: '{chat} में नया संदेश',
  },
  ru: {
    notifyAbout: 'Уведомлять о',
    all_messages: 'Всех сообщениях',
    mentions: 'Только @упоминаниях',
    nothing: 'Не уведомлять',
    indefinite: 'Пока я не включу снова',
    newMessage: 'Новое сообщение в {chat}',
  },
  zh: {
    notifyAbout: '通知内容',
    all_messages: '所有消息',
    mentions: '仅 @提及',
    nothing: '不通知',
    indefinite: '直到我重新开启',
    newMessage: '{chat} 中有新消息',
  },
  'zh-TW': {
    notifyAbout: '通知內容',
    all_messages: '所有訊息',
    mentions: '僅 @提及',
    nothing: '不通知',
    indefinite: '直到我重新開啟',
    newMessage: '{chat} 中有新訊息',
  },
  vi: {
    notifyAbout: 'Thông báo cho tôi về',
    all_messages: 'Tất cả tin nhắn',
    mentions: 'Chỉ @nhắc đến',
    nothing: 'Không thông báo',
    indefinite: 'Đến khi tôi bật lại',
    newMessage: 'Tin nhắn mới trong {chat}',
  },
  id: {
    notifyAbout: 'Beri tahu saya tentang',
    all_messages: 'Semua pesan',
    mentions: 'Hanya @sebutan',
    nothing: 'Tidak ada',
    indefinite: 'Sampai saya mengaktifkannya kembali',
    newMessage: 'Pesan baru di {chat}',
  },
};

export function getChannelNotificationCopy(locale: string) {
  const language = normalizeLocale(locale);
  return {
    ...COPY[language],
    duration: (minutes: number) =>
      new Intl.NumberFormat(language, {
        style: 'unit',
        unit: minutes < 60 ? 'minute' : 'hour',
        unitDisplay: 'long',
      }).format(minutes < 60 ? minutes : minutes / 60),
    message: (chat: string) =>
      COPY[language].newMessage.replace('{chat}', chat),
  };
}
